import { mapGetters } from "vuex";
import _ from "lodash";
import { EventBus } from "@/services/EventBus";
import Loader from "@/components/loader/Loader.vue";
import MapObject from "@/components/map-object/map-object.vue";
import DropdownSelect from "@/components/dropdownSelect/DropdownSelect.vue";
import { getIsAlarmedForMap, setIsAlarmedForMap } from "@/utils";
import { mapObjSizes } from "@/consts/mapObjSizes";

export default {
  name: "Map",
  data() {
    return {
      popup: {
        open: false
      },
      x: null,
      y: null,
      translate: {
        x: 0,
        y: 0
      },
      dragging: false,
      zoom: {
        active: 1,
        list: [
          { name: "25%", value: 0.25 },
          { name: "50%", value: 0.5 },
          { name: "75%", value: 0.75 },
          { name: "100%", value: 1 },
          { name: "150%", value: 1.5 },
          { name: "175%", value: 1.75 },
          { name: "200%", value: 2 },
          { name: "300%", value: 3 },
          { name: "400%", value: 4 },
          { name: "600%", value: 6 },
          { name: "800%", value: 8 }
        ],
        fit: { name: "map.fitIntoWindow", value: "fit" }
      },
      loading: false,

      indicateAlarmsDur: 1000,
      isIndicateAlarms: true,
      isShowIndicateAlarms: getIsAlarmedForMap(
        this.$route.params.mapGroupId,
        this.$route.params.mapId
      ),
      indicateAlarmsTimeout: null,

      camIdFromCellDur: 500,
      camIdFromCellCountMax: 6,
      camIdFromCellCountShow: 0,
      camIdFromCellTimeout: null,
      isIndicateCamIdFromCell: true,
      limitWidthIcon: 20,
      prevZoom: 1
    };
  },
  mounted() {
    this.camIdFromCellTimeout = setTimeout(
      this.onCamIdFromCellTimeout,
      this.camIdFromCellDur
    );

    if (this.isShowIndicateAlarms) {
      this.indicateAlarmsTimeout = setTimeout(
        this.onIndicateAlarmsTimeout,
        this.indicateAlarmsDur
      );
    }

    EventBus.$on("mouseup", this.mouseup);

    EventBus.$on("mousemove", this.mousemove);
  },
  beforeDestroy() {
    clearTimeout(this.indicateAlarmsTimeout);
    this.indicateAlarmsTimeout = null;

    clearTimeout(this.camIdFromCellTimeout);
    this.camIdFromCellTimeout = null;
    this.$store.commit("setCamIdFromCell", "");

    EventBus.$off("mouseup", this.mouseup);
    EventBus.$off("mousemove", this.mouseup);
  },
  computed: {
    ...mapGetters({
      maps: "getMaps",
      cams: "getCams",
      alarmCams: "getAlarmCams",
      activeMap: "getActiveMap",
      grids: "getGrids",
      camIdFromCell: "getCamIdFromCell"
    }),
    map() {
      if (this.activeMap) {
        const activeMap = {
          ...this.activeMap,
          cams: this.activeMap.cams
            .map(cam => ({
              ...this.cams.find(c => c.id == cam.id),
              ...cam
            }))
            .filter(cam => !!cam.status)
        };
        return activeMap;
      }

      return null;
    },
    mapObjects() {
      return [...this.map.cams, ...this.map.mapObjs];
    },
    isShowIndicateAlarmsControl() {
      return this.alarmCams.some(aCam => {
        return this.map.cams.some(mCam => aCam.id === mCam.id);
      });
    },
    zoomList() {
      return [...this.zoom.list, this.zoom.fit];
    },
    objsStyle() {
      /**
       * в пикселях. Учитываем, что ширина равна высоте (круг)
       */
      const defaultWidth = mapObjSizes[this.currentScale].cam.template;
      const minDistanceIcons = 5;
      const minDistanceCenter = defaultWidth + minDistanceIcons;

      const result = {};
      const mapWidth = this.map.width * this.zoom.active;
      const mapHeight = this.map.height * this.zoom.active;
      const mapObjs = [...this.map.cams, ...this.map.mapObjs];
      mapObjs.forEach(obj => {
        const addedObj = {
          id: obj.id,
          x: (mapWidth / 100) * obj.x,
          y: (mapHeight / 100) * obj.y,
          width: defaultWidth,
          left: (mapWidth / 100) * obj.x - defaultWidth / 2,
          top: (mapHeight / 100) * obj.y - defaultWidth / 2
        };
        const resultKey = obj.type === "CAM" ? obj.id : `${obj.type}_${obj.id}`;
        result[resultKey] = addedObj;
      });
      const styleObjArr = Object.values(result);
      styleObjArr.forEach(styleCompare => {
        styleObjArr.forEach(styleObj => {
          const distance = Math.sqrt(
            Math.pow(styleCompare.x - styleObj.x, 2) +
              Math.pow(styleCompare.y - styleObj.y, 2)
          );
          if (distance !== 0) {
            const diffDistCenter = minDistanceCenter - distance;
            if (diffDistCenter > 0) {
              let maxWidthIcon = distance - minDistanceIcons;
              if (maxWidthIcon < this.limitWidthIcon) {
                maxWidthIcon = this.limitWidthIcon;
              }
              if (styleCompare.width > maxWidthIcon) {
                styleCompare.width = maxWidthIcon;
              }
              if (styleObj.width > maxWidthIcon) {
                styleObj.width = maxWidthIcon;
              }
            }
          }
        });
      });
      return result;
    },
    showGridMap() {
      return this.map.width && this.map.width !== 1;
    },
    cellSize() {
      if (!this.showGridMap || !this.map) {
        return;
      }
      return (
        Math.floor(Math.min(this.map.height, this.map.width) * 0.05) *
        this.zoom.active
      );
    },
    currentScale() {
      return this.activeMap.scale || "S";
    },
    levelOnMapSizes() {
      return {
        maxWidth: mapObjSizes[this.currentScale].level.width + "px",
        height: mapObjSizes[this.currentScale].level.height + "px",
        fontSize: mapObjSizes[this.currentScale].level.fontSize + "px"
      };
    },
    arrowOnLevelSize() {
      return {
        width: mapObjSizes[this.currentScale].level.arrowIcon + "px",
        height: mapObjSizes[this.currentScale].level.arrowIcon + "px"
      };
    }
  },
  watch: {
    activeMap: {
      immediate: true,
      handler(map, prev) {
        const isSame =
          map && prev && map.id == prev.id && map.group.id == prev.group.id;

        if (!isSame) {
          this.setDefaultZoom();

          if (map) {
            this.loading = true;
            this.$store
              .dispatch("getMap", {
                mapId: map.id,
                mapGroupId: map.group.id,
                mapName: map.name
              })
              .then(() => {
                this.$store
                  .dispatch("getMapImage", {
                    mapGroupId: this.activeMap.group.id,
                    mapId: this.activeMap.id,
                    activeMap: this.activeMap
                  })
                  .finally(() => {
                    this.setDefaultZoom();
                    this.loading = false;
                  });
              })
              .catch(() => {
                this.setDefaultZoom();
                this.loading = false;
              });
          }
        }
      }
    },
    $route(newRoute) {
      this.isShowIndicateAlarms = getIsAlarmedForMap(
        newRoute.params.mapGroupId,
        newRoute.params.mapId
      );

      clearTimeout(this.indicateAlarmsTimeout);
      this.indicateAlarmsTimeout = null;

      if (this.isShowIndicateAlarms) {
        this.indicateAlarmsTimeout = setTimeout(
          this.onIndicateAlarmsTimeout,
          this.indicateAlarmsDur
        );
      } else {
        this.isIndicateAlarms = true;
      }

      clearTimeout(this.camIdFromCellTimeout);
      this.camIdFromCellTimeout = null;
      this.$store.commit("setCamIdFromCell", "");
    }
  },
  methods: {
    nextZoom(zoomIn = false) {
      this.prevZoom = this.zoom.active;
      let next;

      if (!zoomIn)
        next = _.sortBy(this.zoom.list, item => item.value).find(
          item => item.value > this.zoom.active
        );
      else
        next = _.sortBy(this.zoom.list, item => -item.value).find(
          item => item.value < this.zoom.active
        );

      if (next) {
        this.zoom.active = next.value;
      }
    },
    setZoom(value) {
      if (!this.zoom.list.some(item => item.value === value)) {
        let mapContainer = this.$refs.map;
        let ratio = this.map.width / this.map.height;
        let pratio = mapContainer.clientWidth / mapContainer.clientHeight;

        if (ratio < pratio) {
          value = mapContainer.clientHeight / this.map.height;
        } else {
          value = mapContainer.clientWidth / this.map.width;
        }

        this.zoom.fit.value = value;
      }

      this.translate.x = 0;
      this.translate.y = 0;
      this.zoom.active = value;
    },
    mousemove(e) {
      if (this.dragging) {
        this.translate.x = e.pageX - this.x;
        this.translate.y = e.pageY - this.y;
      }
    },
    mousedown(e) {
      e.preventDefault();
      this.dragging = true;
      this.x = e.pageX - this.translate.x;
      this.y = e.pageY - this.translate.y;
    },
    mouseup(e) {
      this.dragging = false;
      this.onChangeMapPosition();
    },

    mouseWheel(e) {
      this.nextZoom(e.deltaY > 0);
      this.onChangeMapPosition();
    },
    onIndicateAlarmsTimeout() {
      this.isIndicateAlarms = !this.isIndicateAlarms;
      this.indicateAlarmsTimeout = setTimeout(
        this.onIndicateAlarmsTimeout,
        this.indicateAlarmsDur
      );
    },
    onCamIdFromCellTimeout() {
      if (this.camIdFromCellCountShow < this.camIdFromCellCountMax) {
        this.isIndicateCamIdFromCell = !this.isIndicateCamIdFromCell;
        this.camIdFromCellCountShow++;
        this.camIdFromCellTimeout = setTimeout(
          this.onCamIdFromCellTimeout,
          this.camIdFromCellDur
        );
      } else {
        this.isIndicateCamIdFromCell = false;
      }
    },
    onChangeShowIndicateAlarms() {
      setIsAlarmedForMap(
        this.$route.params.mapGroupId,
        this.$route.params.mapId,
        this.isShowIndicateAlarms
      );

      if (this.isShowIndicateAlarms) {
        this.indicateAlarmsTimeout = setTimeout(
          this.onIndicateAlarmsTimeout,
          this.indicateAlarmsDur
        );
      } else {
        clearTimeout(this.indicateAlarmsTimeout);
        this.indicateAlarmsTimeout = null;
        this.isIndicateAlarms = true;
      }
    },
    onChangeMapPosition() {
      this.$nextTick(() => {
        const mapContainer = this.$refs.map;
        const schemeImg = this.$refs.scheme;
        if (!mapContainer || !schemeImg) {
          return;
        }
        const parentPos = mapContainer.getBoundingClientRect();
        const childrenPos = schemeImg.getBoundingClientRect();
        const relativePos = {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        };

        const iconCamHalfW = mapObjSizes[this.currentScale].cam / 2;
        const iconCamHalfH = mapObjSizes[this.currentScale].cam / 2;

        relativePos.top = childrenPos.top + iconCamHalfH - parentPos.top;
        relativePos.bottom =
          parentPos.bottom - childrenPos.bottom - iconCamHalfH;
        relativePos.left = childrenPos.left + iconCamHalfW - parentPos.left;
        relativePos.right = parentPos.right - childrenPos.right - iconCamHalfW;

        const visualRectPersent = {
          left: 0,
          right: 100,
          top: 0,
          bottom: 100
        };

        visualRectPersent.left =
          relativePos.left >= 0
            ? 0
            : Math.abs((relativePos.left / childrenPos.width) * 100);
        visualRectPersent.right =
          relativePos.right >= 0
            ? 100
            : Math.abs(
                ((childrenPos.width + relativePos.right) / childrenPos.width) *
                  100
              );
        visualRectPersent.top =
          relativePos.top >= 0
            ? 0
            : Math.abs((relativePos.top / childrenPos.height) * 100);
        visualRectPersent.bottom =
          relativePos.bottom >= 0
            ? 100
            : Math.abs(
                ((childrenPos.height + relativePos.bottom) /
                  childrenPos.height) *
                  100
              );

        EventBus.$emit("changeMapPosition", visualRectPersent);
      });
    },
    setDefaultZoom() {
      if (this.map && this.$refs.map) {
        let zoom =
          this.map.width > this.$refs.map.clientWidth ||
          this.map.height > this.$refs.map.clientHeight
            ? "fit"
            : 1;
        this.setZoom(zoom);
      }
    },
    getMapObjKey(mObj) {
      return mObj.type === "CAM" ? "cam" + mObj.id : mObj.type + "_" + mObj.id;
    },
    levelPositionOnMap(level, elementKey) {
      let elementWitdh = 0;

      if (this.$refs[elementKey] && this.$refs[elementKey][0]) {
        elementWitdh = this.$refs[elementKey][0].$el.clientWidth;
      }

      return {
        top: ((this.map.height * this.zoom.active) / 100) * level.y + "px",
        left:
          ((this.map.width * this.zoom.active) / 100) * level.x -
          elementWitdh / 2 +
          "px"
      };
    }
  },
  components: {
    Loader,
    MapObject,
    DropdownSelect
  }
};



// WEBPACK FOOTER //
// ./src/modules/map/map.js